import { useCallback, useEffect, useRef, useState } from "react";
import { httpClient } from "../../../shared/api";

/**
 * Discussions tab body for any entity that participates in the polymorphic
 * `/discussions` API (defect / feature / sub_feature / task / meeting).
 *
 * Props:
 *   entityType   — e.g. "feature", "sub_feature", "defect"
 *   entityId     — UUID of the parent record
 *   showToast    — (msg, severity) => void
 *
 * Styling reuses the `dfv2-disc-*` classes shipped with DefectsViewV2.css so
 * the visual treatment matches the Defects drawer.
 */

// Looks backward from the cursor for an unclosed "@word" — returns the query
// text typed so far and the index of the triggering "@", or null if the
// caret isn't currently inside a mention trigger.
function detectMentionTrigger(text, cursorPos) {
  const uptoCursor = text.slice(0, cursorPos);
  const match = uptoCursor.match(/(?:^|\s)@([^\s@]*)$/);
  if (!match) return null;
  const query = match[1];
  const start = cursorPos - query.length - 1;
  return { query, start };
}

export default function EntityDiscussionsTab({ entityType, entityId, showToast }) {
  const [discussions, setDiscussions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ── @mention state ─────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [newMentionIds, setNewMentionIds] = useState([]);
  const [replyMentionIds, setReplyMentionIds] = useState([]);
  const [mentionState, setMentionState] = useState(null); // { target: "new"|"reply", query, start }
  const newTextareaRef = useRef(null);
  const replyTextareaRef = useRef(null);

  useEffect(() => {
    httpClient.get("/users/dropdown")
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]));
  }, []);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const data = await httpClient.get(`/discussions/${entityType}/${entityId}`);
      setDiscussions(Array.isArray(data) ? data : []);
    } catch (err) {
      setDiscussions([]);
      showToast?.(err?.message || "Failed to load discussions", "error");
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, showToast]);

  useEffect(() => { load(); }, [load]);

  const mentionMatches = mentionState
    ? users
        .filter((u) => {
          const q = mentionState.query.toLowerCase();
          if (!q) return true;
          return String(u.display_name || "").toLowerCase().includes(q)
            || String(u.user_name || "").toLowerCase().includes(q);
        })
        .slice(0, 8)
    : [];

  const handleTextChange = (target, el) => {
    const value = el.value;
    const cursorPos = el.selectionStart;
    if (target === "new") setNewComment(value); else setReplyText(value);
    const trigger = detectMentionTrigger(value, cursorPos);
    setMentionState(trigger ? { target, ...trigger } : null);
  };

  const selectMention = (user) => {
    if (!mentionState) return;
    const { target, start, query } = mentionState;
    const isNew = target === "new";
    const text = isNew ? newComment : replyText;
    const cursorPos = start + 1 + query.length;
    const before = text.slice(0, start);
    const after = text.slice(cursorPos);
    const insertLabel = `@${user.display_name || user.user_name} `;
    const nextText = `${before}${insertLabel}${after}`;

    if (isNew) {
      setNewComment(nextText);
      setNewMentionIds((prev) => (prev.includes(user.id) ? prev : [...prev, user.id]));
    } else {
      setReplyText(nextText);
      setReplyMentionIds((prev) => (prev.includes(user.id) ? prev : [...prev, user.id]));
    }
    setMentionState(null);

    requestAnimationFrame(() => {
      const el = isNew ? newTextareaRef.current : replyTextareaRef.current;
      if (el) {
        el.focus();
        const pos = before.length + insertLabel.length;
        el.setSelectionRange(pos, pos);
      }
    });
  };

  const post = async (parentId = null) => {
    const content = parentId ? replyText : newComment;
    if (!content.trim() || !entityId) return;
    setSubmitting(true);
    try {
      await httpClient.post("/discussions", {
        entity_type: entityType,
        entity_id:   entityId,
        content,
        parent_id:   parentId,
        mentioned_user_ids: parentId ? replyMentionIds : newMentionIds,
      });
      if (parentId) { setReplyTo(null); setReplyText(""); setReplyMentionIds([]); }
      else          { setNewComment(""); setNewMentionIds([]); }
      setMentionState(null);
      await load();
    } catch (err) {
      showToast?.(err?.message || "Failed to post comment", "warn");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await httpClient.delete(`/discussions/${id}`);
      await load();
    } catch (err) {
      showToast?.(err?.message || "Failed to delete discussion", "warn");
    }
  };

  const renderMentionDropdown = (target) => {
    if (!mentionState || mentionState.target !== target) return null;
    return (
      <div className="dfv2-disc-mention-list">
        {mentionMatches.length === 0 ? (
          <div className="dfv2-disc-mention-empty">No matching users</div>
        ) : (
          mentionMatches.map((u) => (
            <div
              key={u.id}
              className="dfv2-disc-mention-item"
              onMouseDown={(e) => { e.preventDefault(); selectMention(u); }}
            >
              <span className="dfv2-disc-mention-name">{u.display_name || u.user_name}</span>
              {u.user_name && <span className="dfv2-disc-mention-sub">{u.user_name}</span>}
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div className="dfv2-discussions">
      <div className="dfv2-disc-body">
        {loading && <div className="dfv2-disc-state">Loading…</div>}
        {!loading && discussions !== null && discussions.length === 0 && (
          <div className="dfv2-disc-state">No discussions yet. Be the first to comment!</div>
        )}
        {discussions && discussions.map((d) => (
          <div key={d.id} className="dfv2-disc-thread">
            <div className="dfv2-disc-message">
              <div className="dfv2-disc-message-head">
                <span className="dfv2-disc-message-author">{d.user_name || "Unknown"}</span>
                <span className="dfv2-disc-message-time">{fmtDate(d.created_at)}</span>
              </div>
              <div className="dfv2-disc-message-content">{d.content}</div>
              <div className="dfv2-disc-message-actions">
                <button
                  type="button"
                  className="dfv2-disc-action-btn"
                  onClick={() => setReplyTo(replyTo?.id === d.id ? null : { id: d.id })}
                >
                  Reply
                </button>
                <button
                  type="button"
                  className="dfv2-disc-action-btn dfv2-disc-action-btn--delete"
                  onClick={() => remove(d.id)}
                >
                  Delete
                </button>
              </div>
            </div>

            {(d.replies || []).map((r) => (
              <div key={r.id} className="dfv2-disc-reply">
                <div className="dfv2-disc-message">
                  <div className="dfv2-disc-message-head">
                    <span className="dfv2-disc-message-author">{r.user_name || "Unknown"}</span>
                    <span className="dfv2-disc-message-time">{fmtDate(r.created_at)}</span>
                  </div>
                  <div className="dfv2-disc-message-content">{r.content}</div>
                  <div className="dfv2-disc-message-actions">
                    <button
                      type="button"
                      className="dfv2-disc-action-btn dfv2-disc-action-btn--delete"
                      onClick={() => remove(r.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {replyTo?.id === d.id && (
              <div className="dfv2-disc-reply-input">
                <div className="dfv2-disc-mention-wrap">
                  <textarea
                    ref={replyTextareaRef}
                    className="dfv2-disc-input"
                    placeholder={`Replying to ${d.user_name || "user"}… (@ to mention)`}
                    value={replyText}
                    autoFocus
                    onChange={(e) => handleTextChange("reply", e.target)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (mentionState?.target === "reply") {
                          if (mentionMatches.length > 0) selectMention(mentionMatches[0]);
                        } else {
                          post(d.id);
                        }
                      }
                      if (e.key === "Escape") setMentionState(null);
                    }}
                  />
                  {renderMentionDropdown("reply")}
                </div>
                <div className="dfv2-disc-reply-btns">
                  <button
                    type="button"
                    className="dfv2-disc-submit-btn"
                    onClick={() => post(d.id)}
                    disabled={submitting || !replyText.trim()}
                  >
                    {submitting ? "…" : "Reply"}
                  </button>
                  <button
                    type="button"
                    className="dfv2-disc-cancel-btn"
                    onClick={() => { setReplyTo(null); setReplyText(""); setReplyMentionIds([]); setMentionState(null); }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="dfv2-disc-footer">
        <div className="dfv2-disc-mention-wrap">
          <textarea
            ref={newTextareaRef}
            className="dfv2-disc-input"
            placeholder="Add a comment… (Enter to submit, Shift+Enter for newline, @ to mention)"
            value={newComment}
            onChange={(e) => handleTextChange("new", e.target)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (mentionState?.target === "new") {
                  if (mentionMatches.length > 0) selectMention(mentionMatches[0]);
                } else {
                  post(null);
                }
              }
              if (e.key === "Escape") setMentionState(null);
            }}
          />
          {renderMentionDropdown("new")}
        </div>
        <button
          type="button"
          className="dfv2-disc-submit-btn"
          onClick={() => post(null)}
          disabled={submitting || !newComment.trim()}
        >
          {submitting ? "Posting…" : "Post Comment"}
        </button>
      </div>
    </div>
  );
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")} ${MONTHS[dt.getMonth()]}`;
}
